import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, TextField, Button,
  FormControl, InputLabel, Select, MenuItem,
  Alert, Paper, CircularProgress, List, ListItem,
  ListItemIcon, ListItemText, Collapse,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import apiService from '../services/apiService';

const schema = yup.object().shape({
  businessName: yup.string().trim().required('A szolgáltató neve kötelező').max(100, 'Maximum 100 karakter'),
  categoryId: yup.string().optional(),
  contactName: yup.string().trim().required('A kapcsolattartó neve kötelező').max(100, 'Maximum 100 karakter'),
  phone: yup.string().optional().test('phone-format', 'Érvényes magyar mobiltelefonszám szükséges (pl. +36201234567)', (value) => {
    if (!value) return true;
    return /^\+36(20|30|70)\d{7}$/.test(value);
  }),
  email: yup.string().email('Érvényes email cím szükséges').required('Az email cím kötelező'),
});

const benefits = [
  'Díjmentes csatlakozás – nincs havi elköteleződés',
  'Kiemelt megjelenés a platformon induláskor',
  'Dedikált támogatás a profil kialakításában',
  'Kedvezményes rendszerhasználati díj',
  'Minden tranzakciós költséget a Wedigo vállal',
];

const ProviderLead = () => {
  const [categories, setCategories] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formOpen, setFormOpen] = useState(false);

  const { handleSubmit, control, formState: { errors, isSubmitting } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { businessName: '', categoryId: '', contactName: '', phone: '', email: '' },
  });

  useEffect(() => {
    apiService.get('/categories').then((res) => {
      if (res.success && Array.isArray(res.data?.categories)) setCategories(res.data.categories);
    }).catch(() => {});
  }, []);

  const onSubmit = async (data) => {
    setSubmitError('');
    try {
      const payload = { businessName: data.businessName, contactName: data.contactName, email: data.email, phone: data.phone || undefined, categoryId: data.categoryId || undefined };
      const res = await apiService.post('/provider-leads', payload);
      if (res.success) setSubmitted(true);
      else setSubmitError(res.error || 'Hiba történt az űrlap küldése során.');
    } catch { setSubmitError('Hiba történt az űrlap küldése során. Kérjük, próbálja újra.'); }
  };

  if (submitted) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: 'background.default' }}>
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom fontWeight={700}>Köszönjük a jelentkezést!</Typography>
            <Typography variant="body1" color="text.secondary">Hamarosan felvesszük Önnel a kapcsolatot, és részletesen bemutatjuk a platform működését és a partnerség feltételeit.</Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: 'background.default', py: 6 }}>
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3 }}>
          <Typography variant="h4" align="center" fontWeight={700} gutterBottom sx={{ lineHeight: 1.3 }}>
            Csatlakozz a Wedigo válogatott partneri köréhez
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 3 }}>
            Köszönjük érdeklődést! Az alábbi rövid űrlap kitöltésével fenntarthatod helyed a limitált,
            prémium szolgáltatói körünkben. A kitöltés mindössze <strong>1 percet</strong> vesz igénybe.
          </Typography>
          <List dense sx={{ mb: 3, bgcolor: 'secondary.light', borderRadius: 2, px: 1 }}>
            {benefits.map((benefit) => (
              <ListItem key={benefit} disableGutters sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}><CheckCircleOutlineIcon fontSize="small" color="success" /></ListItemIcon>
                <ListItemText primary={benefit} primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} />
              </ListItem>
            ))}
          </List>

          {!formOpen && (
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => setFormOpen(true)}
              sx={{ py: 1.5, fontWeight: 700, fontSize: '1rem' }}
            >
              Fenntartom a helyem (1 perc)
            </Button>
          )}

          <Collapse in={formOpen} timeout={400}>
            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ mt: formOpen ? 1 : 0 }}>
              {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Controller name="businessName" control={control} render={({ field }) => (<TextField {...field} fullWidth label="Vállalkozás neve" error={!!errors.businessName} helperText={errors.businessName?.message} disabled={isSubmitting} />)} />
                <Controller name="categoryId" control={control} render={({ field }) => (<FormControl fullWidth><InputLabel>Kategória</InputLabel><Select {...field} label="Kategória" disabled={isSubmitting}><MenuItem value=""><em>Válasszon kategóriát (opcionális)</em></MenuItem>{categories.map((cat) => (<MenuItem key={cat.id} value={String(cat.id)}>{cat.name}</MenuItem>))}</Select></FormControl>)} />
                <Controller name="contactName" control={control} render={({ field }) => (<TextField {...field} fullWidth label="Kapcsolattartó neve" error={!!errors.contactName} helperText={errors.contactName?.message} disabled={isSubmitting} />)} />
                <Controller name="phone" control={control} render={({ field }) => (<TextField {...field} fullWidth label="Telefonszám" placeholder="+36201234567" error={!!errors.phone} helperText={errors.phone?.message || 'Érvényes magyar mobilszám (pl. +36201234567)'} disabled={isSubmitting} />)} />
                <Controller name="email" control={control} render={({ field }) => (<TextField {...field} fullWidth label="Email cím" type="email" error={!!errors.email} helperText={errors.email?.message} disabled={isSubmitting} />)} />
                <Button type="submit" variant="contained" size="large" disabled={isSubmitting} sx={{ mt: 1, py: 1.5, fontWeight: 700, fontSize: '1rem' }}>
                  {isSubmitting ? <CircularProgress size={24} /> : 'Jelentkezés beküldése'}
                </Button>
              </Box>
            </Box>
          </Collapse>

          <Typography variant="caption" align="center" display="block" color="text.secondary" sx={{ mt: 3 }}>
            Az adatait bizalmasan kezeljük, és a jelentkezés elküldését követően részletesen bemutatjuk a platform működését és a partnerség feltételeit.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default ProviderLead;
